/**
 * Saved ArcGIS Server / GeoServer connections — Settings › ArcGIS Servers /
 * GeoServer, and the Builder's "Add layers › Saved connection" picker.
 *
 * Two tiers on the server (see `server_connections_router.py`): `list`,
 * `resolve`, `browse` and `resolveService` — reading/using a connection —
 * are open to any signed-in user, since any signed-in user can now reach a
 * Builder/Dashboard screen that adds layers from one. `create`, `update`,
 * `remove` and `test` — managing a connection's stored credentials — stay
 * admin-only. This file adds no gate of its own either way; `ServerConnectionsTab`
 * hides the management controls for a non-admin, matching the server.
 *
 * `resolve` is the one call that returns something credential-bearing (a URL
 * with the connection's username/password embedded as userinfo — see the
 * backend's `server_connections_router.py` for why that shape). Everything
 * else here — list/create/update/test — only ever sees the connection's
 * public fields; the password itself never round-trips through this file at
 * all except as what the admin just typed into the "Add/Edit connection"
 * form and is posting for the FIRST time.
 */

import type { ServerConnection } from '../types';
import { api, ApiError } from './apiClient';

export interface ServerConnectionInput {
  name: string;
  baseUrl: string;
  username: string;
  /** Required on create; optional on update — omitted keeps the saved one. */
  password?: string;
}

export interface ResolvedConnection {
  /** `?token=...` when the server supports one (preferred — see the backend
   *  endpoint's docstring), else `https://user:pass@host/...` as a fallback. */
  authenticatedBaseUrl: string;
  /** Set only when `authenticatedBaseUrl` carries a real token — drives
   *  automatic renewal (see moduleService.renewArcgisConnection). */
  expiresAt?: string | null;
}

/** One MapServer/FeatureServer found while browsing an ArcGIS Server
 *  connection. `name` is empty when the connection is itself a single
 *  service rather than a whole server — see `browse`'s own comment. */
export interface ServerConnectionService {
  name: string;
  type: 'MapServer' | 'FeatureServer';
}

export interface ServerConnectionBrowseResult {
  path: string;
  folders: string[];
  services: ServerConnectionService[];
}

function describeFailure(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.unauthenticated) return 'Your session has expired. Sign in again.';
    if (error.status === 403) return 'Only an administrator can add, edit, delete, or test server connections.';
    if (error.status === 404) return 'This connection no longer exists — it may have just been deleted.';
    if (error.status === 0) return 'Could not reach the server. Check your connection and try again.';
    return error.message;
  }
  return fallback;
}

export const serverConnectionsService = {
  async list(type: 'arcgis' | 'geoserver'): Promise<ServerConnection[]> {
    try {
      return await api.get<ServerConnection[]>(`/server-connections?type=${type}`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not load saved connections.'));
    }
  },

  async create(type: 'arcgis' | 'geoserver', input: ServerConnectionInput): Promise<ServerConnection> {
    try {
      return await api.post<ServerConnection>('/server-connections', { type, ...input });
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not save this connection.'));
    }
  },

  async update(id: string, input: Partial<ServerConnectionInput>): Promise<ServerConnection> {
    try {
      return await api.put<ServerConnection>(`/server-connections/${id}`, input);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not save these changes.'));
    }
  },

  async remove(id: string): Promise<void> {
    try {
      await api.delete<void>(`/server-connections/${id}`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not delete this connection.'));
    }
  },

  async test(id: string): Promise<ServerConnection> {
    try {
      return await api.post<ServerConnection>(`/server-connections/${id}/test`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not test this connection.'));
    }
  },

  /** Only ever called right before browsing/adding a layer from this
   *  connection in the Builder — see AddLayersDialog's "Saved connection". */
  async resolve(id: string): Promise<ResolvedConnection> {
    try {
      return await api.post<ResolvedConnection>(`/server-connections/${id}/resolve`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not connect to this server.'));
    }
  },

  /** One folder's worth of an ArcGIS Server connection's services — pass
   *  `path: ''` for the connection's own root. ArcGIS only. */
  async browse(id: string, path: string): Promise<ServerConnectionBrowseResult> {
    try {
      return await api.get<ServerConnectionBrowseResult>(
        `/server-connections/${id}/browse?path=${encodeURIComponent(path)}`,
      );
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not browse this server.'));
    }
  },

  /**
   * Like `resolve`, but for one specific service found via `browse`.
   *
   * The backend's field is named `service` (a path, e.g. `"Hosted/boundary"`),
   * not `name` — mapped explicitly here rather than renaming
   * `ServerConnectionService.name`, which stays `name` to match `browse`'s
   * own response shape.
   */
  async resolveService(id: string, service: ServerConnectionService): Promise<ResolvedConnection> {
    try {
      return await api.post<ResolvedConnection>(`/server-connections/${id}/resolve-service`, {
        service: service.name,
        type: service.type,
      });
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not connect to this service.'));
    }
  },
};
